/**
 * User persona API client methods (Phase P5).
 *
 * The persona is append-only: every change is a new version row, and GET
 * returns the newest version (or null when the user has none yet).
 */

import { API_CONFIG } from '../../config/api';
import { getToken } from '../tokenStorage';
import { KNOWLEDGE_LEVELS } from '../profileCard';

export type KnowledgeLevel = 'beginner' | 'intermediate' | 'advanced' | 'expert';

export type PersonaSource = 'onboarding' | 'user_edit' | 'chat_confirmed';

/** The persona enum domain (backend services/persona.py KNOWLEDGE_LEVELS,
 * mirrored by the user_personas CHECK constraints): the chip vocabulary of a
 * persona-evolution proposal and the settings picker's option set. Defined
 * ONCE in profileCard (whose coercion owns the same vocabulary; it has zero
 * runtime deps) and re-exported here so persona consumers keep their single
 * import path. */
export { KNOWLEDGE_LEVELS };

/** Newest persona version, as returned by both endpoints. */
export interface UserPersonaRead {
  knowledge_level: KnowledgeLevel;
  source: PersonaSource;
  updated_at: string;
}

export interface PersonaCreateRequest {
  knowledge_level: KnowledgeLevel;
  source: PersonaSource;
}

/**
 * Get the caller's newest persona version.
 * Returns null when the user has no persona yet (200 with a null body).
 */
export async function getPersona(): Promise<UserPersonaRead | null> {
  const token = await getToken();

  if (!token) {
    throw new Error('Authentication required');
  }

  const response = await fetch(`${API_CONFIG.baseURL}/api/v1/users/me/persona`, {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
  });

  if (!response.ok) {
    const error = await response.json();
    if (Array.isArray(error.detail)) {
      const messages = error.detail.map((e: any) => e.msg || e.message).join(', ');
      throw new Error(messages);
    }
    throw new Error(error.detail || 'Failed to get persona');
  }

  return response.json();
}

/**
 * Append a new persona version (rows are never updated).
 * Returns the appended version as the newest read model.
 */
export async function createPersonaVersion(data: PersonaCreateRequest): Promise<UserPersonaRead> {
  const token = await getToken();

  if (!token) {
    throw new Error('Authentication required');
  }

  const response = await fetch(`${API_CONFIG.baseURL}/api/v1/users/me/persona`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify(data),
  });

  if (!response.ok) {
    const error = await response.json();
    if (Array.isArray(error.detail)) {
      const messages = error.detail.map((e: any) => e.msg || e.message).join(', ');
      throw new Error(messages);
    }
    throw new Error(error.detail || 'Failed to save persona');
  }

  return response.json();
}
